<template>
  <section class="cookie_disclosure--container" :class="containerClasses">
    <div class="cookie_disclosure--grid">
      <div>
        <p>Our website uses cookies to improve your experience and personalize content.</p>
        <p>Some cookies may collect personal data, which we use in accordance with our privacy policy.</p>
        <p>You can manage your cookie preferences using the buttons here. You can <a :href="privacyPolicyPath">view our privacy policy here.</a></p>
      </div>
      <div class="cookie_disclosure--button_container">
        <a class="cookie_disclosure--button cookie_disclosure--button-accept" :class="{'cookie_disclosure--button-disabled': isAllCookiesDisabled}"
           href="#" @click.prevent="setUserSetting('all')">
          ACCEPT ALL<span class="cookie_disclosure--element-small_ui_hide"> COOKIES</span>
        </a>
        <a class="cookie_disclosure--button cookie_disclosure--button-reject" :class="{'cookie_disclosure--button-disabled': isRequiredCookiesDisabled}"
           href="#" @click.prevent="setUserSetting('required')">
          ESSENTIAL<span class="cookie_disclosure--element-small_ui_hide"> COOKIES</span> ONLY
        </a>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
/**
 * @description Self-contained cookie disclosure component
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version	1.3.0
 * @copyright	Copyright (C) 2023-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @chanagelog
 *  2023-09-18 (1.0.0) (jhoward)
 *    - Initial release for <cookie-disclosure> component.
 *
 *  2023-10-26 (1.0.1) (jhoward)
 *    - Update copy in <cookie-disclosure>.
 *
 *  2024-01-30 (1.1.0) (jhoward)
 *    - Add "refreshOnRequiredOnly" prop to <cookie-disclosure>.
 *
 *  2024-03-14 (1.1.1) (jhoward)
 *    - Reduce text size and line spacing in <cookie-disclosure>.
 *    - Display buttons side-by-side for mobile in <cookie-disclosure>.
 *    - Add isMinimizeUi prop to <cookie-disclosure> as option to enable smaller text size and tighter layout.
 *
 *  2024-04-16 (1.1.2) (jhoward)
 *    - Fix cookie TTL in <cookie-disclosure> so it isn't treated as a session-duration cookie.
 *
 *  2024-07-17 (1.2.0) (jhoward)
 *    - Add <cookie-disclosure>::loadGtag() to facilitate starting tracking after page load.
 *
 *  2025-05-09 (1.3.0) (jhoward)
 *    - Modify button verbiage and style in <cookie-disclosure>.
 *    - Add a "small UI" in <cookie-disclosure> to facilitate small devices.
 *
 *  2025-07-08 (1.4.0) (jhoward)
 *    - Use GA4 consent mode in <cookie-disclosure>.
 */
import {defineComponent} from 'vue';

interface CookieDisclosureCachedData {
  DATA: '' | 'required' | 'all';
  TIMESTAMP: number;
}

export default defineComponent({
  name: "CookieDisclosure",
  props: {
    isAllCookiesDisabled: {
      type: Boolean,
      default: false
    },
    isRequiredCookiesDisabled: {
      type: Boolean,
      default: false
    },
    refreshOnRequiredOnly: {
      type: Boolean,
      default: false
    },
    privacyPolicyPath: {
      type: String,
      default: '/about-us/privacy-policy'
    },
    isMinimizeUi: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isActive: false,
      storageKey: 'cookie_disclosure',
      storageTTL: 60 * 60 * 24 * 365 * 1000,
      userSetting: null as null | string,
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    window.dataLayer = window.dataLayer || [];


    window.isCookieAllowed = (type) => {
      if (this.userSetting && this.userSetting !== 'all') {
        return type.toLowerCase() === this.userSetting.toLowerCase();
      }

      return true;
    };

    window.setTimeout(() => {
      this.userSetting = this.getUserSetting();
    }, 132);

    if (this.isMinimizeUi) {
      window.addEventListener('resize', () => {
        this.windowWidth = window.innerWidth;
      }, {passive: true});
    }
  },
  watch: {
    userSetting() {
      this.isActive = !this.userSetting || 0 === this.userSetting.length;
    }
  },
  computed: {
    containerClasses() {
      return {
        'cookie_disclosure--container-active': this.isActive,
        'cookie_disclosure--container-minimize_ui': this.isMinimizeUi,
        'cookie_disclosure--container-mobile_ui': this.isMinimizeUi && this.windowWidth < 800,
        'cookie_disclosure--container-small_ui': this.isMinimizeUi && this.windowWidth < 300,
      };
    },
  },
  methods: {
    gtag(): void {
      window.dataLayer.push(arguments);
    },
    // loadGtag(): boolean {
    //   const gtagDummy: HTMLDivElement | null = document.querySelector('#GTM-DUMMY');
    //   if (gtagDummy && 'atob' in window) {
    //     const temporaryContainer = document.createElement('DIV');
    //     temporaryContainer.innerHTML = atob(gtagDummy.innerText);
    //     temporaryContainer.querySelectorAll('script').forEach((script: HTMLScriptElement) => {
    //       const newScript = document.createElement('script');
    //       if (script.src.length) {
    //         newScript.src = script.src;
    //       } else {
    //         newScript.innerHTML = script.innerText;
    //       }
    //       document.body.appendChild(newScript);
    //     });
    //   }
    //
    //   return false;
    // },
    getUserSetting(): string | null {
      const cachedData = window.pepHelperFunctions.getCachedData(this.storageKey, this.storageTTL, true) as false | CookieDisclosureCachedData;
      if (false !== cachedData) {
        return cachedData.DATA;
      }

      return '';
    },
    setUserSetting(value: string) {
      const cookieData	=	JSON.parse(document.querySelector('html')?.dataset.cookie || 'null');
      if (cookieData) {
        window.pepHelperFunctions.setCookie(
          this.storageKey,
          JSON.stringify({TIMESTAMP: new Date().getTime(), DATA: value}),
          new Date((new Date()).getTime() + this.storageTTL),
          cookieData.b
        );
      }
      this.userSetting = value;

      if (this.userSetting === 'required' && this.refreshOnRequiredOnly) {
        setTimeout(() => {
          window.location.reload();
        }, 132);
      } else if (this.userSetting === 'all') {
        //@ts-ignore
        this.gtag('consent', 'update', {
          'ad_storage': 'granted',
          'ad_user_data': 'granted',
          'ad_personalization': 'granted',
          'analytics_storage': 'granted'
        });
      }
    }
  }
})
</script>

<style scoped>
.cookie_disclosure--container {
  position: fixed;
  bottom: 1em;
  left: 0;
  right: 0;
  z-index: 150;
  margin: 0 1em;
  opacity: 0;
  visibility: hidden;
  will-change: transform, opacity, visibility;
  transform: translateY(1em);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), visibility 1ms linear 150ms;
}

.cookie_disclosure--container-minimize_ui {
  font-size: 0.8rem;
}

.cookie_disclosure--container-active {
  opacity: 1;
  visibility: visible;
  transform: none;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), visibility 1ms linear;
}

.cookie_disclosure--grid {
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-gap: 0.5em;
  max-width: 1200px;
  padding: 0.5em 1em;
  margin: 0 auto;
  background: #FFF;
  box-shadow: 0 0 32px rgba(0, 0, 0, 0.54);
  border-radius: 0.25em;
}

.cookie_disclosure--container-minimize_ui .cookie_disclosure--grid {
  max-width: 800px;
}

.cookie_disclosure--button_container {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0.5em;
  align-self: start;
}

.cookie_disclosure--container-mobile_ui .cookie_disclosure--button_container {
  grid-template-columns: 1fr 1fr;
}

.cookie_disclosure--button {
  display: block;
  margin-left: 0.5em;
  padding: 0.5em 0.75em;
  text-decoration: none;
  color: rgb(var(--primary-color, 0, 121, 102));
  text-transform: uppercase;
  background-color: #FFF;
  will-change: background-color;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.cookie_disclosure--button-disabled {
  background-color: #EEE !important;
  color: #808080 !important;
  cursor: default;
}

.cookie_disclosure--button:hover,
.cookie_disclosure--button:focus {
  background-color: #EEE;
}

.cookie_disclosure--button:active {
  background-color: #CCC;
}

.cookie_disclosure--button-accept {
  color: #FFF !important;
  background-color: rgba(var(--primary-color, 0, 121, 102), 0.87);
}

.cookie_disclosure--button-accept:hover,
.cookie_disclosure--button-accept:focus {
  background-color: rgba(var(--primary-color, 0, 121, 102), 1);
}

.cookie_disclosure--button-reject {
  background-color: rgba(var(--secondary-color, 231, 231, 233), 0.87);
}

.cookie_disclosure--container-minimize_ui p {
  margin: 0.25em 0;
}

.cookie_disclosure--container-mobile_ui p {
  margin: 0.5em 0;
}

.cookie_disclosure--container-small_ui .cookie_disclosure--element-small_ui_hide {
  display: none;
}

@media (max-width: 500px) {
  .cookie_disclosure--container-minimize_ui .cookie_disclosure--button_container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 800px) {
  .cookie_disclosure--grid {
    border-radius: initial;
    grid-template-columns: 1fr;
  }
}
</style>